import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { useCallback, useMemo } from 'react'
import { usePermissions } from 'lib/rbac'
import { hubSpotDealIdFromUrl } from 'Integrations/integrationsConfig/hubSpot/InspectorItem/hubSpotDealId'
import { fetchQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { apiQueryClient } from 'features/api/apiQueryClient'

type UseQuoteEditorInspector = () => {
  quoteId: string
  externalIntegrations: {
    visible: boolean
    salesforce: {
      customerLinking: {
        customerId: string
        canUnlink: boolean
        canLink: boolean
      }
      opportunityLinking: {
        canLink: boolean
        canUnlink: boolean
        onLink: (args: { opportunityId: string }) => void
        onUnlink: () => void
        opportunityId?: string
      }
    }
    hubspot: {
      customerLinking: {
        customerId: string
        canUnlink: boolean
        canLink: boolean
      }
      dealLinking: {
        quoteId: string
        customerId: string | undefined
        canLink: boolean
        canUnlink: boolean
        dealId: string | undefined
        dealUrl: string | undefined
        onLink: () => Promise<void>
        onUnlink: () => void
      }
    }
  }
}

export const useQuoteEditorInspector: UseQuoteEditorInspector = () => {
  const ctx = useCubeContext()
  const { can } = usePermissions()
  const canManageIntegrations = can('org.integration:update')
  const canUpdateQuote = can('org.quote:update')

  const salesforceOpportunityLinking = useMemo(() => {
    return {
      canLink: canUpdateQuote,
      canUnlink: canUpdateQuote,
      onLink: (args: { opportunityId: string }) => {
        ctx.mutators.updateData({
          quote: {
            salesforceOpportunityId: args.opportunityId
          }
        })
      },
      onUnlink: () => {
        ctx.mutators.updateData({
          quote: {
            salesforceOpportunityId: undefined
          }
        })
      },
      opportunityId: ctx.queries.rawData.data.quote.salesforceOpportunityId
    }
  }, [
    canUpdateQuote,
    ctx.mutators,
    ctx.queries.rawData.data.quote.salesforceOpportunityId
  ])

  const salesforceCustomerLinking = useMemo(() => {
    return {
      customerId: ctx.queries.rawData.data.common.customerId,
      canUnlink:
        ctx.queries.availableFeatures.common.inspector.unlinkSalesforceCustomer
          .available.enabled && canManageIntegrations,
      canLink:
        ctx.queries.availableFeatures.common.inspector.linkSalesforceCustomer
          .available.enabled && canManageIntegrations
    }
  }, [
    ctx.queries.rawData.data.common.customerId,
    ctx.queries.availableFeatures.common.inspector.unlinkSalesforceCustomer
      .available.enabled,
    ctx.queries.availableFeatures.common.inspector.linkSalesforceCustomer
      .available.enabled,
    canManageIntegrations
  ])

  const hubspotCustomerLinking = useMemo(() => {
    const customerId = ctx.queries.rawData.data.common.customerId
    const enabled = Boolean(customerId) && canManageIntegrations

    return {
      customerId,
      canLink: enabled,
      canUnlink: enabled
    }
  }, [ctx.queries.rawData.data.common.customerId, canManageIntegrations])

  const quoteId = ctx.queries.rawData.data.common.id
  const quoteIntegrationIds = ctx.queries.rawData.data.quote.integrationIds
  const { updateData } = ctx.mutators

  const refreshQuoteIntegrationIds = useCallback(async () => {
    const quote = await fetchQuery(
      apiQueryClient,
      dashboard20240730Client.getQuote,
      { id: quoteId },
      { staleTime: 0 }
    ).catch(() => null)

    if (!quote) {
      return
    }

    updateData({
      quote: { integrationIds: quote.integrationIds ?? [] }
    })
  }, [updateData, quoteId])

  const dropHubSpotQuoteLink = useCallback(() => {
    updateData({
      quote: {
        integrationIds: quoteIntegrationIds.filter(
          integration => integration.service !== 'HubSpot'
        )
      }
    })
  }, [updateData, quoteIntegrationIds])

  const hubspotDealLinking = useMemo(() => {
    const link = quoteIntegrationIds.find(
      integration => integration.service === 'HubSpot'
    )

    return {
      quoteId,
      customerId: ctx.queries.rawData.data.common.customerId || undefined,
      canLink: canManageIntegrations,
      canUnlink: canManageIntegrations,
      dealId: hubSpotDealIdFromUrl(link?.url),
      dealUrl: link?.url,
      onLink: refreshQuoteIntegrationIds,
      onUnlink: dropHubSpotQuoteLink
    }
  }, [
    quoteId,
    ctx.queries.rawData.data.common.customerId,
    quoteIntegrationIds,
    canManageIntegrations,
    refreshQuoteIntegrationIds,
    dropHubSpotQuoteLink
  ])

  return {
    quoteId: ctx.queries.rawData.data.common.id,
    externalIntegrations: {
      visible:
        ctx.queries.availableFeatures.quote.integrations.available.visible,
      salesforce: {
        customerLinking: salesforceCustomerLinking,
        opportunityLinking: salesforceOpportunityLinking
      },
      hubspot: {
        customerLinking: hubspotCustomerLinking,
        dealLinking: hubspotDealLinking
      }
    }
  }
}
